<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { salesReviewNavigationState } from '../composables/salesReviewNavigationState';
import { propertySections } from '../composables/salesPropertyFormOptions';
import SalesReviewCorrectionCountBadge from './SalesReviewCorrectionCountBadge.vue';

const props = defineProps({
  activeSection: { type: String, required: true },
  isComplete: { type: Function, required: true },
  reviewMode: { type: Boolean, default: false },
  reviewCount: { type: Number, default: 0 },
  referenceLayout: { type: Boolean, default: false },
  sectionChecks: { type: Object, default: () => ({}) },
  sectionKeys: { type: Array, required: true },
});

const emit = defineEmits(['select']);
const { t } = useI18n();
const sections = computed(() =>
  propertySections(t).filter(section => props.sectionKeys.includes(section.key))
);
const firstIncompleteIndex = computed(() =>
  sections.value.findIndex(section => !props.isComplete(section.key))
);
const isSelectable = sectionKey => {
  if (props.reviewMode) return true;

  const lastAvailableIndex =
    firstIncompleteIndex.value === -1
      ? sections.value.length - 1
      : firstIncompleteIndex.value;
  return (
    sections.value.findIndex(section => section.key === sectionKey) <=
    lastAvailableIndex
  );
};
const navigationState = section =>
  salesReviewNavigationState({
    active: props.activeSection === section.key,
    checks: props.sectionChecks[section.key] || [],
    isComplete: props.isComplete(section.key),
    reviewMode: props.reviewMode,
  });
const statusClass = section => {
  const classes = {
    active: 'border-lais-primary bg-lais-primary-50 text-lais-primary',
    default:
      'border-lais-border-soft bg-white text-lais-text hover:border-lais-primary/50',
    pending: 'border-red-200 bg-red-50 text-red-600 hover:border-red-300',
    saved:
      'border-lais-border-soft bg-white text-lais-text hover:border-lais-primary/50',
  };

  return classes[navigationState(section).state];
};
const statusIconClass = section => {
  const classes = {
    active: 'text-lais-primary',
    default: 'text-lais-text-muted',
    pending: 'text-red-600',
    saved: 'text-lais-primary',
  };

  return classes[navigationState(section).state];
};
const referenceStatusClass = section => {
  const state = navigationState(section).state;
  if (state === 'active') return 'bg-[#EDF8F7] text-[#009F94]';
  if (state === 'saved') return 'text-[#475569]';

  return 'text-[#A7B7C1]';
};
const referenceStatusIcon = section =>
  navigationState(section).icon === 'check'
    ? 'i-lucide-check'
    : 'i-lucide-pencil';
const referenceTextClass = section =>
  section.key === 'documents'
    ? 'whitespace-nowrap text-base font-bold leading-6'
    : 'whitespace-normal text-base font-bold leading-6';
</script>

<template>
  <nav
    class="grid gap-2 lg:sticky lg:top-4 lg:block"
    :class="referenceLayout ? 'gap-0 !static !top-auto' : ''"
    :aria-label="t('SALES_PROPERTY.ARIA.SECTIONS')"
    data-test="sales-property-section-nav"
  >
    <SalesReviewCorrectionCountBadge v-if="reviewMode" :count="reviewCount" />
    <button
      v-for="section in sections"
      :key="section.key"
      type="button"
      class="flex min-w-0 items-center gap-3 rounded-lais border px-3 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-60 lg:w-full"
      :class="
        referenceLayout
          ? `min-h-12 rounded-none border-0 !px-5 !py-2 text-base font-bold !gap-4 ${referenceStatusClass(section)}`
          : statusClass(section)
      "
      :disabled="!isSelectable(section.key)"
      @click="emit('select', section.key)"
    >
      <span
        class="size-4 shrink-0"
        :class="[
          referenceLayout ? 'size-5' : '',
          referenceLayout ? referenceStatusIcon(section) : section.icon,
        ]"
        :data-test="`property-section-state-${section.key}`"
        aria-hidden="true"
      />
      <span
        class="min-w-0 flex-1"
        :class="
          referenceLayout
            ? referenceTextClass(section)
            : 'text-[13px] font-semibold'
        "
      >
        {{ section.label }}
      </span>
      <span
        v-if="!referenceLayout"
        class="size-4 shrink-0"
        :class="[
          navigationState(section).icon === 'check'
            ? 'i-lucide-check'
            : 'i-lucide-pencil',
          statusIconClass(section),
        ]"
        :data-test="`review-state-${section.key}`"
        aria-hidden="true"
      />
    </button>
  </nav>
</template>
